import { Link } from 'react-router';
import { Button, Section } from '@p1plug/ui';
import { Code, Snippet } from '../doc';
import { createOrder, firstRequest, firstResponse } from '../samples';

const FLOW: [string, string, string][] = [
  ['You', 'Create an order', 'One API call with your customer and their address.'],
  ['P1Plug', 'Ships a device', 'Picked, linked to the order and sent to your customer.'],
  ['Your customer', 'Plugs it in', 'No app, no code, no Wi-Fi. It activates itself.'],
  ['P1Plug', 'Delivers the data', 'Smart-meter readings and lifecycle events, to your systems.'],
];

const PATHS: [string, string, string][] = [
  ['/docs/quickstart', 'Quickstart', 'From credentials to your first order and your first event, in six steps.'],
  ['/docs/authentication', 'Authentication', 'API keys to start, OAuth client credentials with scopes for production.'],
  [
    '/docs/webhooks',
    'Webhooks and events',
    'Signed, ordered, at-least-once, and a cursor API that replays after an outage.',
  ],
  ['/docs/sandbox', 'Sandbox', 'Test the whole lifecycle, including the failures, without hardware.'],
  ['/docs/errors', 'Errors', 'Every error code: what it means, whether to retry and how to fix it.'],
  ['/reference', 'API reference', 'Every operation, generated from the API contract.'],
];

export function Home() {
  return (
    <article className="doc">
      <header className="dev-hero">
        <p className="dev-eyebrow">P1Plug for developers</p>
        <h1 className="dev-hero-title">Integrate once. P1Plug handles the operational complexity.</h1>
        <p className="p1x-lead">
          P1Plug delivers real-time smart-meter data from your customers’ homes to your systems. You make one
          integration: orders in, events and meter data out. Hardware, logistics, connectivity and device management are
          ours.
        </p>
        <div className="p1x-row dev-hero-actions">
          <Button label="Start the quickstart" to="/docs/quickstart" />
          <Button label="Browse the API reference" to="/reference" variant="secondary" />
        </div>
      </header>

      <Section title="How the integration works">
        <ol className="dev-flow" role="list">
          {FLOW.map(([who, what, how], i) => (
            <li key={what}>
              <span className="dev-flow-who">
                {i + 1} · {who}
              </span>
              <span className="dev-flow-what">{what}</span>
              <span className="dev-flow-how">{how}</span>
            </li>
          ))}
        </ol>
        <p className="doc-p">
          You never talk to a device. You talk to a <Link to="/docs/concepts">meter point</Link>: the stable handle for
          one customer’s meter, which keeps its history when a device is replaced.
        </p>
      </Section>

      <Section
        title="Your first request"
        lead="Every request carries a bearer credential and is scoped to your organisation. This one tells you who you are."
      >
        <div className="dev-pair">
          <Code samples={firstRequest} />
          <Snippet title="200 OK" code={firstResponse} />
        </div>
      </Section>

      <Section
        title="Order a P1Plug"
        lead="The order is the whole integration surface for fulfilment. It is safe to retry, and its response already contains the meter point you will read data from."
      >
        <Code samples={createOrder} />
        <p className="doc-p">
          From here P1Plug takes over. You follow along through <Link to="/docs/webhooks">events</Link>:{' '}
          <code className="p1x-inline-code">order.status_changed</code> as the parcel moves,{' '}
          <code className="p1x-inline-code">installation.activated</code> when your customer plugs it in, then{' '}
          <code className="p1x-inline-code">telemetry.electricity</code>.
        </p>
      </Section>

      <Section title="Where to go next">
        <ul className="dev-paths" role="list">
          {PATHS.map(([to, title, text]) => (
            <li key={to}>
              <Link to={to}>{title}</Link>
              <span>{text}</span>
            </li>
          ))}
        </ul>
      </Section>
    </article>
  );
}
